"use client";

import { createWallet, loginWithCode, sendLoginCode } from "@opensea/embedded-wallet-sdk";
import { useCallback, useRef } from "react";
import { useSnapshot } from "valtio";
import { EMBEDDED_WALLET_CONNECTOR_ID } from "../../../../../lib/wagmi";
import { useConnectors, useDisconnect } from "../../../../../polychain/hooks";
import { useSiweAdapter } from "../../../../../providers/SiweProvider";
import { useTrackError } from "../../../../../providers/TrackErrorProvider";
import { AccountActions } from "../../../../../stores/accountStore";
import { ConnectModalActions, ConnectModalState } from "../../../../../stores/connectModalStore";
import { useAcceptTerms } from "../../../../hooks/useAcceptTerms";
import { AcknowledgeEmailSignupStep } from "./AcknowledgeEmailSignupStep";
import { AuthCodeInputStep } from "./AuthCodeInputStep";
import { EmailInputStep } from "./EmailInputStep";
export const EmailLogin = ({
  switchOnConnect,
  isLinking,
  connect
}) => {
  const siweAdapter = useSiweAdapter();
  const {
    getConnectorStrict
  } = useConnectors();
  const trackError = useTrackError();
  const {
    step
  } = useSnapshot(ConnectModalState);
  const {
    disconnect
  } = useDisconnect();
  const onSubmitEmail = async email => {
    const embeddedWalletConnector = getConnectorStrict(EMBEDDED_WALLET_CONNECTOR_ID);
    await embeddedWalletConnector.baseConnector.setup?.();
    try {
      await disconnect(embeddedWalletConnector, {
        endSession: false
      });
    } catch (error) {
      trackError(error, {
        tags: {
          source: "EmailLogin"
        }
      });
    }

    // TODO: Error handling
    await sendLoginCode(email);
    ConnectModalActions.setStep({
      id: "enter-email-auth-code",
      email
    });
  };
  const connectEmbeddedWallet = useCallback(async () => {
    const embeddedWalletConnector_0 = getConnectorStrict(EMBEDDED_WALLET_CONNECTOR_ID);
    const address = await connect(embeddedWalletConnector_0, {
      switchOnConnect
    });

    // Authenticate with token exchange
    if (accessTokenRef.current && siweAdapter?.exchangeToken && !isLinking) {
      try {
        await siweAdapter.exchangeToken({
          token: accessTokenRef.current
        });
        AccountActions.setAuthenticated(true);
      } catch (error_0) {
        trackError(error_0, {
          tags: {
            source: "EmailLogin"
          }
        });
        // Continue without authenticated state
      }
    }

    // The flow ends once the user is connected (see ConnectFlow.tsx)
    // Reset auth modal state to initial state
    ConnectModalActions.reset();
    return address;
  }, [connect, getConnectorStrict, siweAdapter, trackError, switchOnConnect, isLinking]);
  const {
    acceptTerms
  } = useAcceptTerms();
  const onAcknowledgeEmailSignup = async () => {
    if (step.id !== "acknowledge-email-signup") {
      throw new Error("Invalid step for acknowledging email signup");
    }

    // TODO: Error handling
    await createWallet();
    const address_0 = await connectEmbeddedWallet();
    await acceptTerms({
      address: address_0
    });
  };
  const accessTokenRef = useRef(undefined);
  const onSubmitAuthCode = useCallback(async authCode => {
    if (step.id !== "enter-email-auth-code") {
      throw new Error("Invalid step for auth code submission");
    }
    const loginWithCodeResponse = await loginWithCode(authCode);
    if (typeof loginWithCodeResponse === "string") {
      throw new Error(loginWithCodeResponse);
    }
    accessTokenRef.current = loginWithCodeResponse.accessToken;
    if (loginWithCodeResponse.isNewUser) {
      ConnectModalActions.setStep({
        id: "acknowledge-email-signup",
        email: step.email
      });
      return;
    }
    await connectEmbeddedWallet();
  }, [connectEmbeddedWallet, step]);
  switch (step.id) {
    case "pick-login-option":
      return <EmailInputStep initialEmail={step.email} onSubmit={onSubmitEmail} />;
    case "verify-auth-code":
    case "enter-email-auth-code":
      return <AuthCodeInputStep email={step.email} onSubmit={onSubmitAuthCode} />;
    case "acknowledge-email-signup":
      return <AcknowledgeEmailSignupStep initialEmail={step.email} onSubmit={onAcknowledgeEmailSignup} />;
    default:
      return null;
  }
};